import React, { useEffect, useRef, useState } from "react";
import { RotateCcw, MessageCircle, ArrowUpRight } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { agents } from "@/lib/agentsData";
import { convos, imageDemos } from "@/lib/demosData";

const HASH = /^#demos?(?:-(\d+))?(-play)?$/;

const Bubble = ({ from, text }) => {
  if (from === "system")
    return (
      <div className="demo-in flex justify-center">
        <span className="font-mono text-[10px] tracking-[0.14em] uppercase text-[#8A6A3E] bg-[#FBF3E1] border border-[#E8CE95]/60 rounded-full px-3 py-1 text-center">
          {text}
        </span>
      </div>
    );
  const ai = from === "ai";
  return (
    <div className={`demo-in flex ${ai ? "justify-start" : "justify-end"}`}>
      <div
        className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-[14.5px] leading-[1.5] ${
          ai ? "bg-[var(--night)] text-white rounded-bl-md" : "bg-white border border-[var(--line)] text-[var(--ink)] rounded-br-md"
        }`}
      >
        <span className={`block font-mono text-[9px] tracking-[0.2em] uppercase mb-1 ${ai ? "text-[#E8CE95]" : "text-[var(--ink-faint)]"}`}>
          {ai ? "Duaura AI" : "Customer"}
        </span>
        {text}
      </div>
    </div>
  );
};

const Typing = () => (
  <div className="flex gap-1 px-4 py-3 w-fit rounded-2xl bg-white border border-[var(--line)]" data-testid="demo-typing">
    {[0, 1, 2].map((d) => (
      <span key={d} className="w-1.5 h-1.5 rounded-full bg-[var(--ink-faint)] animate-bounce" style={{ animationDelay: `${d * 120}ms` }} />
    ))}
  </div>
);

const ReplayButton = ({ onClick }) => (
  <button
    type="button"
    data-testid="demo-play-btn"
    onClick={onClick}
    className="btn-cobalt rounded-full text-[15px] font-medium pl-5 pr-6 py-3 inline-flex items-center gap-2 shadow-[0_12px_32px_-10px_rgba(24,37,63,0.55)] hover:-translate-y-0.5 transition-transform"
  >
    <RotateCcw className="w-4 h-4" />
    Replay
  </button>
);

const BookButton = ({ onClick, testId = "demo-book-btn" }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    className="rounded-full text-[15px] font-semibold pl-5 pr-4 py-3 inline-flex items-center gap-2 text-[var(--night)] hover:-translate-y-0.5 transition-transform shadow-[0_12px_32px_-10px_rgba(199,155,85,0.7)]"
    style={{ background: "linear-gradient(135deg, #F3DFAE 0%, #E8CE95 45%, #C79B55 100%)" }}
  >
    Book free call
    <ArrowUpRight className="w-4 h-4" />
  </button>
);

const DemoPlayer = ({ idx, onBook }) => {
  const { channel, lines } = convos[idx];
  const [shown, setShown] = useState(0);
  const [playing, setPlaying] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    setShown(0);
    setPlaying(true);
  }, [idx]);

  useEffect(() => {
    if (!playing) return;
    if (shown >= lines.length) {
      setPlaying(false);
      return;
    }
    const t = setTimeout(() => setShown((n) => n + 1), shown === 0 ? 400 : 1400);
    return () => clearTimeout(t);
  }, [playing, shown, lines.length]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [shown, playing]);

  const done = shown >= lines.length;
  const start = () => {
    setShown(0);
    setPlaying(true);
  };

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="px-5 sm:px-7 py-4 border-b border-[var(--line)] bg-white">
        <div>
          <div className="font-serif text-[22px] text-[var(--ink)] leading-tight">{agents[idx].name}</div>
          <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[var(--ink-faint)] mt-1">{channel}</div>
        </div>
      </div>
      <div data-testid="demo-transcript" className="flex-1 min-h-0 overflow-y-auto px-5 sm:px-7 py-6 space-y-3 bg-[var(--porcelain-mist)]">
        {lines.slice(0, shown).map(([from, text], i) => (
          <Bubble key={i} from={from} text={text} />
        ))}
        {playing && !done && <Typing />}
        {done && !playing && (
          <div className="flex flex-wrap justify-center gap-3 pt-4">
            <ReplayButton onClick={start} />
            <BookButton onClick={onBook} />
          </div>
        )}
        <div ref={endRef} />
      </div>
      <p className="px-5 sm:px-7 py-2.5 text-[11px] text-[var(--ink-faint)] bg-white border-t border-[var(--line)]">
        Sample conversation for illustration. Your AI is set up around your business, voice, and tools.
      </p>
    </div>
  );
};

const ImageDemo = ({ idx, onBook }) => {
  const { desktop, phone, alt, cta } = imageDemos[agents[idx].name];
  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="px-5 sm:px-7 py-4 border-b border-[var(--line)] bg-white">
        <div className="font-serif text-[22px] text-[var(--ink)] leading-tight">{agents[idx].name}</div>
        <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[var(--ink-faint)] mt-1">Sample output</div>
      </div>
      <div data-testid="demo-image-area" className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-6 bg-[var(--porcelain-mist)]">
        <picture>
          <source media="(min-width: 641px)" srcSet={desktop} />
          <img data-testid="demo-image" src={phone} alt={alt} className="block w-full h-auto rounded-xl border border-[var(--line)]" />
        </picture>
        <div className="mt-5 flex flex-wrap justify-center gap-3">
        {cta && (
          <a
            data-testid="demo-messenger-btn"
            href={cta.href}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-cobalt rounded-full text-[14px] font-medium pl-4 pr-5 py-3 inline-flex items-center gap-2"
          >
            <MessageCircle className="w-4 h-4" />
            {cta.label}
          </a>
        )}
        <BookButton onClick={onBook} testId="demo-image-book-btn" />
        </div>
      </div>
    </div>
  );
};

export default function DemoGallery() {
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const check = () => {
      const m = window.location.hash.match(HASH);
      if (!m) return;
      setIdx(m[1] ? Math.min(Number(m[1]), agents.length - 1) : 0);
      setOpen(true);
    };
    check();
    window.addEventListener("hashchange", check);
    return () => window.removeEventListener("hashchange", check);
  }, []);

  const close = () => {
    setOpen(false);
    if (HASH.test(window.location.hash)) window.history.replaceState(null, "", window.location.pathname + window.location.search);
  };

  const book = () => {
    setOpen(false);
    window.location.hash = "#book";
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent
        data-testid="demo-gallery"
        className="max-w-[1100px] w-[96vw] h-[90vh] p-0 gap-0 overflow-hidden rounded-2xl border border-[var(--line)] bg-white flex flex-col [&>button]:text-white [&>button]:z-10"
      >
        <header className="relative bg-[var(--night)] text-white px-6 sm:px-8 py-5 overflow-hidden shrink-0">
          <div className="absolute -top-24 -right-16 w-[300px] h-[300px] orb opacity-60" />
          <span className="relative font-mono text-[10.5px] tracking-[0.22em] uppercase text-[#E8CE95]">Live demos</span>
          <DialogTitle className="relative font-serif font-medium text-[26px] sm:text-[30px] leading-tight mt-1 text-white tracking-normal">
            See our AI agents{" "}
            <span className="italic font-italic-serif text-white/70">at work.</span>
          </DialogTitle>
          <DialogDescription className="sr-only">Pick an agent and play a sample conversation.</DialogDescription>
        </header>

        <div className="flex-1 min-h-0 flex flex-col md:flex-row">
          <nav className="shrink-0 md:w-[260px] border-b md:border-b-0 md:border-r border-[var(--line)] bg-white overflow-x-auto md:overflow-y-auto">
            <ul className="flex md:flex-col gap-1 p-3 pr-8 md:pr-3">
              {agents.map((a, i) => (
                <li key={a.name} className="shrink-0">
                  <button
                    type="button"
                    data-testid={`demo-tab-${i}`}
                    onClick={() => setIdx(i)}
                    className={`w-full text-left whitespace-nowrap rounded-xl px-4 py-2.5 text-[14px] transition-colors ${
                      i === idx ? "bg-[var(--night)] text-white" : "text-[var(--ink-soft)] hover:bg-[var(--porcelain-mist)]"
                    }`}
                  >
                    {a.name}
                  </button>
                </li>
              ))}
            </ul>
          </nav>
          <div className="flex-1 min-h-0">
            {imageDemos[agents[idx].name] ? <ImageDemo idx={idx} onBook={book} /> : <DemoPlayer idx={idx} onBook={book} />}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
